// acneScarsRemovalData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[] | any;
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const physio: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Best Physiotherapy at Home Services in Dubai | Elite Body Home",
            "description": "Professional physiotherapy services at your home in Dubai. Pain relief, injury recovery, and rehabilitation with personalized treatment plans. FREE consultation available. Book your appointment today.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/physiotherapy-at-home-dubai/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Best Physiotherapy at Home Services in Dubai | Elite Body Home",
                "description": "Professional physiotherapy services at your home in Dubai. Pain relief, injury recovery, and rehabilitation with personalized treatment plans. FREE consultation available. Book your appointment today.",
                "url": "https://elitebodyhome.com/services/physiotherapy-at-home-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Physiotherapy.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Physiotherapy.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Physiotherapy at Home Dubai",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Best Physiotherapy at Home Services in Dubai | Elite Body Home",
                "description": "Professional physiotherapy services at your home in Dubai. Pain relief, injury recovery, and rehabilitation with personalized treatment plans. FREE consultation available. Book your appointment today.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Physiotherapy.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Physiotherapy at Home Dubai"],
        "hero": {
            "title": "Physiotherapy at Home Services",
            "subtitle": "",
            "description": "At Elite Body Home, experience the convenience of expert Physiotherapy at Home in Dubai. Whether you're recovering from an injury, coping with chronic pain, or just want rehabilitation, we'll create a customized treatment plan for you. Avoid the trouble of travel and appointments, and allow our specialists to assist you in regaining shape, strength and health at home."
        },
        "howItWorks": {
            "title": "Physiotherapy at Home Process",
            "description": "Physiotherapy home treatment is a step-by-step procedure which helps the body in mobility improvement, pain reduction and healing through specific exercises and treatments. Our specialists develop a treatment plan customized for each patient's specific needs."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "Initial Assessment",
                    "desc": "Comprehensive assessment by a qualified physiotherapist including physical checkup, medical history review, and problem identification.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Customized Treatment Plan",
                    "desc": "Personalized treatment plan tailored based on assessment, including exercises, manual therapy, electrotherapy and movement education.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "Home Visits",
                    "desc": "The physiotherapist comes to your home on scheduled days to provide treatment and ensure exercises are done correctly.",
                    "icon": "fa-solid fa-house-chimney-medical"
                },
                {
                    "title": "Progress Monitoring",
                    "desc": "Regular progress assessments with necessary adjustments to your treatment plan for optimal recovery.",
                    "icon": "fa-solid fa-chart-line"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Home-based Physiotherapy",
            "subheading": "Why home physiotherapy makes a difference",
            "para": "Home physiotherapy provides many benefits, especially for patients who are unable to visit a clinic or want treatment in the comfort of their home:",
            "items": [
                {
                    "title": "Convenience & Comfort",
                    "desc": "Treatment at home eliminates travel complexities, ideal for patients with mobility issues or busy schedules.",
                    "icon": "fa-solid fa-couch"
                },
                {
                    "title": "Personalized Care",
                    "desc": "Individual attention from the physiotherapist with treatment tailored to your specific needs.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "Faster Recovery",
                    "desc": "Comfortable home environment allows consistent exercise performance to speed up recovery.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "Reduced Infection Risk",
                    "desc": "Avoid exposure to pathogens in clinical settings, especially important post-surgery or for immunocompromised patients.",
                    "icon": "fa-solid fa-shield-virus"
                }
            ]
        },
        "eligibility": {
            "heading": "Who Can Benefit?",
            "subheading": "",
            "para": "Home physiotherapy is useful for various patients, especially those with mobility issues or chronic conditions:",
            "items": [
                {
                    "title": "Post-Surgery Recovery",
                    "desc": "Patients rehabilitating after orthopedic procedures like joint replacement or spinal surgery.",
                    "icon": "fa-solid fa-bed-pulse"
                },
                {
                    "title": "Chronic Pain Conditions",
                    "desc": "Patients with arthritis, back pain or sciatica can improve quality of life with home therapy.",
                    "icon": "fa-solid fa-bone"
                },
                {
                    "title": "Neurological Conditions",
                    "desc": "People with stroke, multiple sclerosis or Parkinson's disease requiring regular therapy.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "Elderly Patients",
                    "desc": "Older adults needing help with mobility, balance and strength improvement.",
                    "icon": "fa-solid fa-person-cane"
                }
            ]
        },
        "preparationTips": {
            "heading": "How to Prepare",
            "subheading": "",
            "para": "Prepare for effective and comfortable physiotherapy sessions at home:",
            "items": [
                {
                    "title": "Create Suitable Space",
                    "desc": "Prepare a quiet, clear space in your house with enough room for safe exercises.",
                    "icon": "fa-solid fa-border-none"
                },
                {
                    "title": "Wear Comfortable Clothing",
                    "desc": "Choose loose, breathable clothing that enables easy movement during exercises.",
                    "icon": "fa-solid fa-shirt"
                },
                {
                    "title": "Prepare Medical Records",
                    "desc": "Have your medical history, prescriptions or imaging results ready for review.",
                    "icon": "fa-solid fa-folder-open"
                },
                {
                    "title": "Arrange Equipment",
                    "desc": "Have any special equipment like exercise bands or mats available if needed.",
                    "icon": "fa-solid fa-dumbbell"
                }
            ]
        },
        "results": {
            "heading": "Expected Results",
            "subheading": "",
            "para": "With consistent home physiotherapy, patients can expect significant improvements:",
            "items": [
                {
                    "title": "Pain Reduction",
                    "desc": "Effective pain management through targeted exercises and therapies.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Improved Mobility",
                    "desc": "Restored movement and flexibility in affected areas.",
                    "icon": "fa-solid fa-person-walking"
                },
                {
                    "title": "Strength Recovery",
                    "desc": "Regained muscle strength and endurance through progressive exercises.",
                    "icon": "fa-solid fa-dumbbell"
                },
                {
                    "title": "Functional Independence",
                    "desc": "Improved ability to perform daily activities without assistance.",
                    "icon": "fa-solid fa-wheelchair-move"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Aftercare Recommendations",
            "subheading": "",
            "para": "Maintain your recovery progress with these aftercare tips:",
            "items": [
                {
                    "title": "Continue Home Exercises",
                    "desc": "Follow prescribed home exercise program to maintain progress.",
                    "icon": "fa-solid fa-person-running"
                },
                {
                    "title": "Schedule Follow-ups",
                    "desc": "Periodic check-ins with your physiotherapist to assess progress.",
                    "icon": "fa-solid fa-calendar-days"
                },
                {
                    "title": "Maintain Healthy Habits",
                    "desc": "Proper nutrition, hydration and rest support recovery.",
                    "icon": "fa-solid fa-apple-whole"
                },
                {
                    "title": "Monitor Progress",
                    "desc": "Keep track of improvements and report any concerns to your therapist.",
                    "icon": "fa-solid fa-clipboard-list"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How long does a home physiotherapy session last?",
                    "answer": "Sessions typically last between 45 minutes to 1 hour, depending on your condition and treatment plan."
                },
                {
                    "question": "How many physiotherapy sessions will I need?",
                    "answer": "The number varies based on your condition and recovery progress. Your physiotherapist will recommend a treatment plan after initial assessment."
                },
                {
                    "question": "Does insurance cover home physiotherapy in Dubai?",
                    "answer": "Many health insurance providers in Dubai cover home physiotherapy. We recommend checking with your insurance company for specific coverage details."
                },
                {
                    "question": "What equipment is needed for home physiotherapy?",
                    "answer": "Most sessions require minimal equipment like exercise bands or mats. Your physiotherapist will advise if any special equipment is needed for your treatment."
                },
                {
                    "question": "Can home physiotherapy be as effective as clinic sessions?",
                    "answer": "Yes, home physiotherapy can be equally effective as the same professional treatments are provided in the comfort of your home environment."
                },
                {
                    "question": "How quickly can I see results from physiotherapy?",
                    "answer": "Results vary by individual and condition, but many patients notice improvements within 2-4 weeks of consistent therapy."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/New-Service-Banner-Image-6-scaled.jpg",
            "beforeAfter": []
        },
        "product": {
            "id": 352,
            "title": "Physiotherapy at Home Consultation Package",
            "price": 0,
            "description": "Professional physiotherapy assessment and initial treatment session at your home in Dubai. Our expert physiotherapists create personalized treatment plans for pain relief, injury recovery and rehabilitation."
        }
    },
    "ar": {
        "url": "",
        "meta": {
            "title": "أفضل خدمات العلاج الطبيعي المنزلي في دبي | ايليت بودي هوم",
            "description": "خدمات علاج طبيعي منزلية احترافية في دبي. تخفيف الآلام، التعافي من الإصابات وإعادة التأهيل بخطط علاج مخصصة. استشارة مجانية متاحة. احجز موعدك اليوم.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/ar/services/physiotherapy-at-home-dubai/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "أفضل خدمات العلاج الطبيعي المنزلي في دبي | ايليت بودي هوم",
                "description": "خدمات علاج طبيعي منزلية احترافية في دبي. تخفيف الآلام، التعافي من الإصابات وإعادة التأهيل بخطط علاج مخصصة. استشارة مجانية متاحة. احجز موعدك اليوم.",
                "url": "https://elitebodyhome.com/ar/services/physiotherapy-at-home-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Physiotherapy.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Physiotherapy.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "العلاج الطبيعي المنزلي دبي",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "أفضل خدمات العلاج الطبيعي المنزلي في دبي | ايليت بودي هوم",
                "description": "خدمات علاج طبيعي منزلية احترافية في دبي. تخفيف الآلام، التعافي من الإصابات وإعادة التأهيل بخطط علاج مخصصة. استشارة مجانية متاحة. احجز موعدك اليوم.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Physiotherapy.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "العلاج الطبيعي المنزلي دبي"],
        "hero": {
            "title": "خدمات العلاج الطبيعي المنزلي",
            "subtitle": "",
            "description": "اختبر راحة العلاج الطبيعي المنزلي في دبي مع ايليت بودي هوم. يوفر أخصائيو العلاج الطبيعي المعتمدون لدينا رعاية شخصية تُقدم مباشرةً في منزلك، مع ضمان الحصول على نفس جودة العلاج العالية كما في العيادات. سواء كنت تتعافى من إصابة، أو تتعامل مع آلام مزمنة، أو تبحث عن إعادة تأهيل، فإننا نُخصص كل جلسة بما يتناسب مع حالتك الفردية. ودع عناء التنقل والمواعيد واسمح لخبرائنا بمساعدتك على استعادة الحركة، وتحسين القوة، وتحقيق أفضل مستوى من الصحة، وكل ذلك من راحة منزلك."
        },
        "howItWorks": {
            "title": "خطوات العلاج الطبيعي في المنزل",
            "description": "تُنفذ جلسات العلاج الطبيعي في المنزل من خلال خطوات منظمة تهدف إلى استعادة الحركة، وتخفيف الألم، وتسريع التعافي عبر تمارين وعلاج مخصص لحالة كل مريض."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "التقييم الأولي",
                    "desc": "تبدأ العملية بتقييم شامل من قبل أخصائي علاج طبيعي معتمد. يتضمن ذلك فحصًا جسديًا، ومراجعة التاريخ الطبي، وتحديد السبب الرئيسي للمشكلة.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "خطة علاجية مخصصة",
                    "desc": "استنادًا إلى التقييم، تُصمم خطة علاج شخصية، يمكن أن تتضمن تمارين علاجية، وعلاج يدوي، وجلسات كهربائية، وتوعية بأساليب الحركة الصحيحة.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "زيارات منزلية",
                    "desc": "يقوم أخصائي العلاج الطبيعي بزيارات مجدولة إلى منزل المريض لتطبيق العلاج. كما يتأكد من أداء التمارين بالشكل الصحيح وتعديل الخطة حسب الحاجة.",
                    "icon": "fa-solid fa-house-chimney-medical"
                },
                {
                    "title": "المتابعة والتعديل المستمر",
                    "desc": "يتم مراقبة التقدم بانتظام وتحديث خطة العلاج عند الحاجة، لضمان التعافي الأمثل وتخفيف الألم بشكل فعال.",
                    "icon": "fa-solid fa-chart-line"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد العلاج الطبيعي المنزلي",
            "subheading": "لماذا يعد العلاج المنزلي خيارًا ممتازًا",
            "para": "يمنح اختيار العلاج الطبيعي في المنزل المرضى العديد من المزايا، خاصةً لمن يعانون من صعوبة في التنقل أو يفضلون تلقي العلاج من راحة منزلهم:",
            "items": [
                {
                    "title": "الراحة والسهولة",
                    "desc": "يوفر العلاج في المنزل عناء التنقل إلى العيادة، وهو مثالي للمرضى الذين يعانون من مشكلات في الحركة أو لديهم جدول مزدحم.",
                    "icon": "fa-solid fa-couch"
                },
                {
                    "title": "رعاية مخصصة",
                    "desc": "يحصل المريض على اهتمام فردي مباشر من الأخصائي، مع تصميم خطة علاجية تتناسب تمامًا مع حالته الصحية واحتياجاته الخاصة.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "تسريع عملية التعافي",
                    "desc": "يساعد أداء التمارين في بيئة مألوفة على الالتزام بها ويعزز من سرعة الشفاء.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "تقليل خطر التعرض لعدوى",
                    "desc": "يقلل العلاج في المنزل من فرص التعرض للعدوى، خصوصًا للمرضى المتعافين من عمليات جراحية أو أصحاب المناعة الضعيفة.",
                    "icon": "fa-solid fa-shield-virus"
                }
            ]
        },
        "eligibility": {
            "heading": "من المؤهل للعلاج الطبيعي المنزلي؟",
            "subheading": "",
            "para": "يناسب العلاج الطبيعي في المنزل مجموعة واسعة من الحالات، وهو مفيد بشكل خاص للأشخاص الذين يعانون من صعوبات في الحركة أو أمراض مزمنة:",
            "items": [
                {
                    "title": "التعافي بعد العمليات الجراحية",
                    "desc": "يستفيد مرضى جراحة العظام، مثل جراحة استبدال المفاصل أو جراحة العمود الفقري، بشكل كبير من العلاج الطبيعي المنزلي لدعم التعافي السريع وتحسين الحركة.",
                    "icon": "fa-solid fa-bed-pulse"
                },
                {
                    "title": "حالات الألم المزمن",
                    "desc": "يمكن للأشخاص المصابين بآلام مزمنة مثل التهاب المفاصل، أو آلام أسفل الظهر أو عرق النسا، تحسين جودة حياتهم من خلال جلسات منتظمة في المنزل.",
                    "icon": "fa-solid fa-bone"
                },
                {
                    "title": "الأمراض العصبية",
                    "desc": "يحتاج المرضى الذين يعانون من أمراض عصبية مثل السكتة الدماغية، أو التصلب المتعدد أو مرض باركنسون، إلى علاج طبيعي مستمر في بيئة مريحة.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "كبار السن",
                    "desc": "يواجه كبار السن مشكلات في الحركة أو التوازن أو فقدان القوة الجسدية. يمكنهم الاستفادة من خطة علاجية مصممة خصيصًا لهم من المنزل.",
                    "icon": "fa-solid fa-person-cane"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد لجلسات العلاج الطبيعي المنزلي",
            "subheading": "",
            "para": "يُنصح باتباع بعض الخطوات التحضيرية البسيطة، لضمان فاعلية جلسات العلاج الطبيعي في المنزل وراحتك أثناء العلاج:",
            "items": [
                {
                    "title": "تهيئة مساحة مناسبة",
                    "desc": "قم بتجهيز مساحة هادئة ومفتوحة في منزلك ليتمكن أخصائي العلاج الطبيعي من إجراء الجلسات فيها. يُفضل أن تكون هذه المساحة واسعة بما يكفي لأداء التمارين بأمان.",
                    "icon": "fa-solid fa-border-none"
                },
                {
                    "title": "ارتدِ ملابس مريحة",
                    "desc": "اختر ملابس فضفاضة وقابلة للتمدد، تسمح بحرية الحركة أثناء التمارين.",
                    "icon": "fa-solid fa-shirt"
                },
                {
                    "title": "تحضير السجلات الطبية",
                    "desc": "احرص على تجهيز جميع التقارير الطبية، والأدوية الموصوفة، وصور الأشعة مثل الأشعة السينية أو الرنين المغناطيسي، ليتمكن الأخصائي من مراجعتها قبل بدء الخطة العلاجية.",
                    "icon": "fa-solid fa-folder-open"
                },
                {
                    "title": "تجهيز الأدوات اللازمة",
                    "desc": "إذا كانت الخطة العلاجية تتطلب استخدام أدوات محددة مثل أشرطة التمارين أو سجادة أو دراجة ثابتة، يُفضل تجهيزها مسبقًا لضمان سير الجلسة بسلاسة.",
                    "icon": "fa-solid fa-dumbbell"
                }
            ]
        },
        "results": {
            "heading": "النتائج المتوقعة",
            "subheading": "",
            "para": "مع الالتزام بجلسات العلاج الطبيعي المنزلية، يمكن للمرضى توقع تحسن ملحوظ في حالتهم:",
            "items": [
                {
                    "title": "تخفيف الألم",
                    "desc": "إدارة فعالة للألم من خلال تمارين وعلاجات موجهة.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "تحسين الحركة",
                    "desc": "استعادة النطاق الطبيعي للحركة والمرونة في المناطق المصابة.",
                    "icon": "fa-solid fa-person-walking"
                },
                {
                    "title": "استعادة القوة",
                    "desc": "تحسين قوة العضلات وقدرتها على التحمل من خلال تمارين تدريجية.",
                    "icon": "fa-solid fa-dumbbell"
                },
                {
                    "title": "الاستقلالية الوظيفية",
                    "desc": "تحسن القدرة على أداء الأنشطة اليومية دون مساعدة.",
                    "icon": "fa-solid fa-wheelchair-move"
                }
            ]
        },
        "aftercareTips": {
            "heading": "نصائح ما بعد العلاج",
            "subheading": "",
            "para": "حافظ على تقدمك في التعافي من خلال هذه النصائح:",
            "items": [
                {
                    "title": "الاستمرار في التمارين المنزلية",
                    "desc": "التزم ببرنامج التمارين المنزلية الموصوف للحفاظ على التقدم المحرز.",
                    "icon": "fa-solid fa-person-running"
                },
                {
                    "title": "جدولة زيارات المتابعة",
                    "desc": "حدد مواعيد متابعة دورية مع أخصائي العلاج الطبيعي لتقييم التقدم.",
                    "icon": "fa-solid fa-calendar-days"
                },
                {
                    "title": "الحفاظ على عادات صحية",
                    "desc": "التغذية السليمة، الترطيب الكافي والراحة تدعم عملية التعافي.",
                    "icon": "fa-solid fa-apple-whole"
                },
                {
                    "title": "مراقبة التقدم",
                    "desc": "تابع التحسن وأبلغ عن أي مخاوف لمعالجك.",
                    "icon": "fa-solid fa-clipboard-list"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة المتكررة",
            "subheading": "",
            "items": [
                {
                    "question": "كم تستغرق جلسة العلاج الطبيعي؟",
                    "answer": "تتراوح مدة الجلسة بين 45 دقيقة إلى ساعة عادةً، وذلك بناءً على طبيعة الحالة وتعقيد خطة العلاج."
                },
                {
                    "question": "كم عدد الجلسات التي أحتاجها؟",
                    "answer": "يعتمد عدد الجلسات على حالة المريض واستجابته للعلاج. سيقوم أخصائي العلاج الطبيعي بتحديد عدد الجلسات الموصى بها بعد الاستشارة الأولية."
                },
                {
                    "question": "هل يغطي التأمين الصحي جلسات العلاج الطبيعي المنزلي؟",
                    "answer": "تغطي العديد من شركات التأمين الصحي في دبي خدمات العلاج الطبيعي، بما في ذلك الجلسات المنزلية. يُنصح بالتواصل مع شركة التأمين الخاصة بك للتأكد من تفاصيل التغطية."
                },
                {
                    "question": "ما المعدات التي أحتاجها للعلاج الطبيعي في المنزل؟",
                    "answer": "تختلف المعدات المطلوبة حسب خطة العلاج، وتشمل في الغالب أدوات بسيطة مثل أربطة المقاومة، والسجاد الرياضي، والأوزان الخفيفة. سيقوم الأخصائي بإرشادك إلى أي معدات إضافية قد تحتاجها."
                },
                {
                    "question": "هل العلاج الطبيعي المنزلي فعال مثل جلسات العيادة؟",
                    "answer": "نعم، يمكن أن يكون العلاج الطبيعي المنزلي بنفس الفعالية حيث يتم تقديم العلاجات الاحترافية نفسها في راحة منزلك."
                },
                {
                    "question": "متى يمكنني رؤية النتائج من العلاج الطبيعي؟",
                    "answer": "تختلف النتائج حسب الفرد والحالة، لكن العديد من المرضى يلاحظون تحسنًا خلال 2-4 أسابيع من العلاج المنتظم."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    }
}